import React, { useState } from "react";
import {
  GameCondition,
  PlayerInfo,
  TeamInfoResponse,
} from "../api/copenhunt/v1/copenhunt_pb";
import { shapeSvgMap, Shape } from "../utils";
import "./TeamsDisplay.css";
import CustomModal from "./CustomModal";

interface TeamsDisplayProps {
  playersByTeam: { [teamId: string]: PlayerInfo[] };
  teamColors: { [teamId: string]: string };
  teamNumbers: { [teamId: string]: number };
  teamInfoById: { [teamId: string]: TeamInfoResponse };
  teamIds: string[];
  objectiveShapes: { [objectiveId: string]: Shape };
  onDeletePlayer: (playerId: string, teamId: string) => void;
  onRemoveObjective: (
    teamId: string,
    objectiveId: string,
    canReclaim: boolean
  ) => void;
  currentTime: number;
  onDeleteTeam: (teamId: string) => void;
  gameCondition: GameCondition;
}

const TeamsDisplay: React.FC<TeamsDisplayProps> = ({
  playersByTeam,
  teamColors,
  teamNumbers,
  teamInfoById,
  teamIds,
  objectiveShapes,
  onDeletePlayer,
  onRemoveObjective,
  currentTime,
  onDeleteTeam,
  gameCondition,
}) => {
  const [areAllTeamsExpanded, setAreAllTeamsExpanded] =
    useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [removalContext, setRemovalContext] = useState<{
    teamId: string;
    objectiveId: string;
  } | null>(null);

  const toggleAllTeamsExpansion = () => {
    setAreAllTeamsExpanded((prevState) => !prevState);
  };

  const handleOpenModal = (teamId: string, objectiveId: string) => {
    setRemovalContext({ teamId, objectiveId });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setRemovalContext(null);
  };

  const handleConfirmRemoval = (canReclaim: boolean) => {
    if (removalContext) {
      onRemoveObjective(
        removalContext.teamId,
        removalContext.objectiveId,
        canReclaim
      );
    }
    handleCloseModal();
  };

  const renderObjectiveIcon = (objectiveId: string) => {
    return objectiveId ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 48 48"
        width="24"
        height="24"
        dangerouslySetInnerHTML={{
          __html: `<g transform="scale(1.5)">${
            shapeSvgMap[objectiveShapes[objectiveId]]
          }</g>`,
        }}
      ></svg>
    ) : null;
  };

  return (
    <div className="teams-display-container">
      {teamIds.map((teamId) => {
        const team = teamInfoById[teamId];
        const players = playersByTeam[teamId] || [];
        const isExpanded = areAllTeamsExpanded;

        return (
          <div className={`team-card ${teamColors[teamId]}`} key={teamId}>
            <div className="team-header" onClick={toggleAllTeamsExpansion}>
              <div className="team-name">
                <strong>{`Team ${teamNumbers[teamId]}: ${team.name}`}</strong>
                {gameCondition === GameCondition.NOT_STARTED ? (
                  <button
                    onClick={() => onDeleteTeam(teamId)}
                    className="remove-button-small"
                  >
                    &times;
                  </button>
                ) : null}
              </div>
              {team.objectiveId && (
                <div className="team-objective">
                  {renderObjectiveIcon(team.objectiveId)}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenModal(teamId, team.objectiveId);
                    }}
                    className="remove-button-small"
                  >
                    &times;
                  </button>
                </div>
              )}
              <span
                className={`accordion-icon ${isExpanded ? "expanded" : ""}`}
              >
                &#9660;
              </span>
            </div>
            {isExpanded && (
              <div className="team-details">
                <ul>
                  {players.map((player) => {
                    const latestCoordinates =
                      player.coordinates.length > 0
                        ? player.coordinates[player.coordinates.length - 1]
                        : null;
                    const delta =
                      currentTime - (latestCoordinates?.timestamp ?? 0);
                    const classNames = delta > 120 ? "inactive" : "";

                    return (
                      <li key={player.playerId} className={classNames}>
                        <span>{player.name}</span>
                        <button
                          onClick={() =>
                            onDeletePlayer(player.playerId, teamId)
                          }
                          className="remove-button-small"
                        >
                          &times;
                        </button>
                      </li>
                    );
                  })}
                  {players.length === 0 && <li>No players yet.</li>}
                </ul>
              </div>
            )}
          </div>
        );
      })}
      {removalContext && (
        <CustomModal
          isOpen={isModalOpen}
          onRequestClose={handleCloseModal}
          title="Remove Objective"
        >
          <div>
            <p>Can the team reclaim this objective later?</p>
            <div className="modal-buttons">
              <button
                className="modal-button yes"
                onClick={() => handleConfirmRemoval(true)}
              >
                Yes
              </button>
              <button
                className="modal-button no"
                onClick={() => handleConfirmRemoval(false)}
              >
                No
              </button>
              <button
                className="modal-button cancel"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </CustomModal>
      )}
    </div>
  );
};

export default TeamsDisplay;
